/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect } from "react";
import { Col, Row } from "react-awesome-styled-grid";
import { Else, If, Then } from "react-if";
import { ListLoader, Text, theme } from "@nordcloud/gnui";
import { GroupsForResourceSortFields, SortOrder } from "~/generated/graphql";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination";
import { getNoSearchResultsMessage } from "~/components/SearchInput";
import { useQueryState, useGetSearchPhrase } from "~/hooks";
import { isEmpty } from "~/tools";
import { useGetGroupsForResource } from "../../hooks/useGetGroupsForResource/useGetGroupsForResource";
import { ResourceDetailsResourceGroupsQuery } from "../../types/ResourceDetailsResourceGroupsQuery";
import { ResourceDetailsResourceGroupItem } from "./ResourceDetailsResourceGroupItem";
import { prepareResourceDetailsResourceGroupsFilter } from "./resourceDetailsResourceGroupsFilterBuilders";
import { ResourceDetailsResourceGroupsGridColumns } from "./ResourceDetailsResourceGroupsGridColumns";
import { ResourceDetailsResourceGroupsLoader } from "./ResourceDetailsResourceGroupsLoader";

type Props = {
  resourceId: string;
  resourceName: string;
  onCountChange?: (count: number) => void;
};

export function ResourceDetailsResourceGroupsTable({
  resourceId,
  resourceName,
  onCountChange,
}: Props) {
  const { queryState } = useQueryState<ResourceDetailsResourceGroupsQuery>();
  const { phrase, isSearchAllowed } = useGetSearchPhrase(
    queryState.searchPhrase
  );

  const {
    data: resourceGroups,
    loading,
    count,
    refetch,
  } = useGetGroupsForResource({
    resourceId: resourceId ?? "",
    page: queryState.page ?? "1",
    limit: queryState.limit ?? "20",
    sort: {
      field: queryState.field ?? GroupsForResourceSortFields.Name,
      order: queryState.order ?? SortOrder.Asc,
    },
    filter: prepareResourceDetailsResourceGroupsFilter(queryState, phrase),
  });
  useEffect(() => {
    onCountChange?.(count);
  }, [count, onCountChange]);

  return (
    <>
      <If condition={!isSearchAllowed}>
        <Then>
          <NoData hasIcon message={getNoSearchResultsMessage()} />
        </Then>
        <Else>
          <LoaderWrap
            loading={loading}
            Component={() => (
              <ListLoader>
                <ResourceDetailsResourceGroupsLoader />
              </ListLoader>
            )}
          >
            <If condition={isEmpty(resourceGroups)}>
              <Then>
                <NoData hasIcon message={"There are no Resource Groups."} />
              </Then>
              <Else>
                <>
                  <Row>
                    <Col>
                      <ResourceDetailsResourceGroupsGridColumns hasPadding>
                        <div />
                        <Text
                          size="sm"
                          weight="medium"
                          m={0}
                          color={theme.color.text.text02}
                        >
                          Resource Group Name
                        </Text>
                        <Text
                          size="sm"
                          weight="medium"
                          m={0}
                          color={theme.color.text.text02}
                        >
                          Date Created
                        </Text>
                        <Text
                          size="sm"
                          weight="medium"
                          m={0}
                          color={theme.color.text.text02}
                        >
                          Date Updated
                        </Text>
                        <Text
                          size="sm"
                          weight="medium"
                          m={0}
                          color={theme.color.text.text02}
                        >
                          Created by
                        </Text>
                        <Text
                          size="sm"
                          weight="medium"
                          m={0}
                          color={theme.color.text.text02}
                        >
                          Number of Plans
                        </Text>
                      </ResourceDetailsResourceGroupsGridColumns>
                    </Col>
                  </Row>
                  {resourceGroups.map((resourceGroup) => (
                    <ResourceDetailsResourceGroupItem
                      key={resourceGroup.id}
                      resourceGroup={resourceGroup}
                      resourceName={resourceName}
                      refetch={refetch}
                    />
                  ))}
                  <Pagination count={count} />
                </>
              </Else>
            </If>
          </LoaderWrap>
        </Else>
      </If>
    </>
  );
}
